import React from 'react';
import { useHistory } from 'react-router-dom';
import { message, Input, Select, Button, Typography, Spin, Checkbox } from 'antd';
import { useShallow } from 'zustand/react/shallow';
import { useQuery } from '../../helpers';
import useMappingStore from './stores/useMappingStore';
import useMappingUiStore from './stores/useMappingUiStore';
import MappingControl from './components/MappingControl';
import { ReactComponent as BackIcon } from 'svg/icon/left-arrow-short.svg';
import facebookIcon from 'svg/common/feeds/fb.png';
import googleIcon from 'svg/common/feeds/google.png';
import yaIcon from 'svg/common/feeds/ya.png';
import mytargetIcon from 'svg/common/feeds/mytarget.png';
import { FrownOutlined } from '@ant-design/icons';

import styles from './MappingPage.module.css';

const { Text } = Typography;

const selectIcon = {
  width: '16px',
  height: '16px',
  marginRight: '8px',
};

const feedTemplateText = 'Map the fields between the data source and output feed you want.';
const unsupportedFeedTemplateText =
  'Given feed template is not supported. Please try to choose another feed type from the select field.';

function getIcon(iconType) {
  switch (iconType) {
    case 'google':
      return <img style={selectIcon} alt="" src={googleIcon} />;
    case 'facebook':
      return <img style={selectIcon} alt="" src={facebookIcon} />;
    case 'yandex':
      return <img style={selectIcon} alt="" src={yaIcon} />;
    case 'mytarget':
      return <img style={selectIcon} alt="" src={mytargetIcon} />;
    case 'csv':
      return <span style={{ ...selectIcon, fontSize: '12px', fontWeight: 'bold' }}>CSV</span>;
    default:
      return null;
  }
}

export default function MappingLayout() {
  const urlHistory = useHistory();
  const query = useQuery().get('organization');
  const organizationQuery = query ? `?organization=${query}` : '';

  const {
    name,
    projectId,
    ruleId,
    mappingId,
    mappings,
    feedTypes,
    formatType,
    selectedFeedTypeId,
    feedTemplate,
    productFields,
    formatTypes,
  } = useMappingStore(
    useShallow((s) => ({
      name: s.name,
      projectId: s.projectId,
      ruleId: s.ruleId,
      mappingId: s.mappingId,
      mappings: s.mappings,
      feedTypes: s.feedTypes,
      formatType: s.formatType,
      selectedFeedTypeId: s.selectedFeedTypeId,
      feedTemplate: s.feedTemplate,
      productFields: s.productFields,
      formatTypes: s.formatTypes,
    })),
  );

  const { loading, clickedOnSave, showCheckbox, checkboxValue } = useMappingUiStore(
    useShallow((s) => ({
      loading: s.loading,
      clickedOnSave: s.clickedOnSave,
      showCheckbox: s.showCheckbox,
      checkboxValue: s.checkboxValue,
    })),
  );

  const { setName, setFeedTypeId, setFormatType, setMappings, saveMappings } =
    useMappingStore.getState();
  const { setClickedOnSave, setShowCheckbox, setCheckboxValue } = useMappingUiStore.getState();

  const isXmlFormat = formatType === 'yml';

  const isCustomCsv =
    selectedFeedTypeId === 'custom_csv' ||
    (feedTemplate && feedTemplate.fields && feedTemplate.fields.length === 0);

  const exit = () => {
    urlHistory.push(`/project/${projectId}/rule/${ruleId}${organizationQuery}`);
  };

  const handleSave = () => {
    setClickedOnSave(true);
    saveMappings((id) => {
      if (!mappingId) urlHistory.push(`/project/${projectId}/mapping/${id}${organizationQuery}`);
    });
  };

  const changeFeedType = (value) => {
    setFeedTypeId(value);

    const CHECKBOX_FEED_TYPES = [
      'yandex_market_simple',
      'yandex_market_advanced',
      'mytarget_xml_simple',
      'mytarget_xml_advanced',
    ];

    if (CHECKBOX_FEED_TYPES.indexOf(value) > -1) {
      setShowCheckbox(true);
    } else {
      setShowCheckbox(false);
      setCheckboxValue(false);
    }
  };

  const onMappingValueChange = (mapping, value) => {
    if (mapping) {
      mapping.value = value;
    }
  };

  const addNewMapping = (type = 'field') => {
    const newMappings = [...mappings];
    newMappings.push({
      feedFieldId: isCustomCsv ? '' : undefined,
      value: '',
      required: false,
      type,
    });
    setMappings(newMappings);
  };

  const deleteMapping = (mapping) => {
    const newMappings = mappings.filter((m) => m !== mapping);
    setMappings([...newMappings]);
  };

  return (
    <div className={`scroll-panel scroll-panel-standart ${styles.container}`}>
      {loading && !feedTemplate ? (
        <div className="centered-container">
          <Spin size="large" />
        </div>
      ) : (
        <>
          <div className={styles.header}>
            <Button shape="circle" type="text" onClick={exit} icon={<BackIcon />} />
            <h3 className={styles.title}>Mappings</h3>
          </div>
          <div className={styles.main}>
            <div className={`${styles.flexContainer} ${styles.upperInputsBlock}`}>
              <Input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                }}
                placeholder="Feed template name"
                className={`${styles.input} ${styles.centeredInput}`}
              />
              <Select
                value={selectedFeedTypeId}
                onChange={changeFeedType}
                className={`${styles.input} ${styles.centeredInput} ${styles.select} ${!feedTemplate ? styles.errorSelect : ''}`}
              >
                {feedTypes.map((feed) => (
                  <Select.Option key={feed.id} value={feed.id}>
                    <div className={styles.flexContainer}>
                      {getIcon(feed.icon)}
                      <span>{feed.name}</span>
                    </div>
                  </Select.Option>
                ))}
              </Select>

              <Select
                value={formatType}
                onChange={setFormatType}
                disabled={formatTypes.length <= 1}
                className={`${styles.input} ${styles.centeredInput} ${styles.select}`}
              >
                {formatTypes.length &&
                  formatTypes.map((format, i) => (
                    <Select.Option key={`field-${i}`} value={format}>
                      {format}
                    </Select.Option>
                  ))}
              </Select>
            </div>
            <Text className={styles.description}>
              {feedTemplate ? feedTemplateText : unsupportedFeedTemplateText}
            </Text>

            {showCheckbox && (
              <Checkbox
                checked={checkboxValue}
                onChange={(e) => setCheckboxValue(e.target.checked)}
                className={`${styles.input} ${styles.centeredInput}`}
                style={{ marginTop: '20px', height: 'inherit !important', maxWidth: '600px', border: '0' }}
              >
                Auto add original params (or rest of params If some are used in feed template)
              </Checkbox>
            )}

            {feedTemplate ? (
              <MappingControl
                isXmlFormat={isXmlFormat}
                onChange={onMappingValueChange}
                feedFields={feedTemplate.fields}
                clickedOnSave={clickedOnSave}
                addNewMapping={addNewMapping}
                deleteMapping={deleteMapping}
                save={handleSave}
              />
            ) : (
              <FrownOutlined className={styles.emptyFeedTemplateIcon} />
            )}
          </div>
        </>
      )}
    </div>
  );
}
